import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'url-polyfill';
import './index.css';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import * as serviceWorker from './serviceWorker';
import Logger from 'js-logger';
import support from 'support';
import CssBaseline from '@material-ui/core/CssBaseline';
import { initialize, getTokenFromUrl, officeDialogActionInterceptor } from 'utils/initUtils';
import { loadLocales, getCurrentLanguage } from 'utils/localeUtils';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from './theme';
import MainLayout from 'layouts/mainLayout';
import { MainProvider } from 'mainProvider';
import { updateOfficeContext } from 'redux/officeContextSlice';
import LoadingAnimation from 'components/LoadingAnimation';
import createStore from 'redux/store';

Logger.useDefaults();
Logger.setLevel(Logger.DEBUG);
Logger.info(`Lifesize Outlook Plugin Application (Build ${support.version})`);
const initAndRender = async (store: Store<object>) => {
  try {
    await support.init();
    const token = getTokenFromUrl() || '';
    await loadLocales(getCurrentLanguage() as string);
    await initialize(token, store);
    renderApp(store);
  } catch (e) {
    Logger.warn(e);
    renderApp(store);
  }
};

const renderApp = (store: Store<object>) => {
  const ErrorBoundary = support.getReactErrorBoundary();
  ReactDOM.render(
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <MainProvider>
            <MainLayout />
          </MainProvider>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>,
    document.getElementById('root')
  );
};

const renderLoader = () => {
  ReactDOM.render(
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LoadingAnimation />
    </ThemeProvider>,
    document.getElementById('root')
  );
};

serviceWorker.register({
  onUpdate: async (registration: any) => {
    // We want to run this code only if we detect a new service worker is
    // waiting to be activated.
    // Details about it: https://developers.google.com/web/fundamentals/primers/service-workers/lifecycle
    if (registration && registration.waiting) {
      Logger.log('New service worker found: update and reload');
      await registration.unregister();
      // Once the service worker is unregistered, we can reload the page to let
      // the browser download a fresh copy of our app (invalidating the cache)
      window.location.reload(true);
    }
  }
});

/* Render application after Office initializes */
Office.onReady((info) => {
  Logger.log(`Office.js is now ready in ${info.host} on ${info.platform}`);
  const isOfficeWeb = info.platform === Office.PlatformType.OfficeOnline;
  const store = createStore();
  (window as any).store = store;
  store.dispatch(
    updateOfficeContext({
      host: info.host,
      platform: info.platform,
      isOfficeWeb,
      isOfficeReady: !!(info.host && info.platform)
    })
  );
  /**
   * This function will intercept following actions.
   * LOGIN_ACTION
   * LOGOUT_ACTION
   */
  officeDialogActionInterceptor();
  initAndRender(store);
});

/* Show Loader until office is initialized. */
renderLoader();
